/**
 * DemandForecastDaily 断面の描画専用値型と pure builder。
 *
 * index.tsx → ForecastCard/ForecastChart (chart 系列)・KomaCard/KomaGrid
 * (48コマ表) の素通しを 1 箇所に畳む
 * (Design Note tmp/designing-feature/refactor-demand-forecast-section-view-model.md
 * D1〜D9)。次を単一の計算箇所にする:
 * - gate 済み系列 (todayActual は当日のみ / trendForecast は hasTrendReflect のみ /
 *   currentKoma も当日のみ / pastActuals の FETCH_FAILED 降格〈D6〉)
 * - 系列存在述語 (ForecastCard の凡例構築と ForecastChart の描画可否が同じ helper を
 *   同じ入力で二重計算していたもの、`slotHelpers.ts` 参照) を 1 回だけ計算する
 * - 同名別概念の `baselineSlots` を `editing.hasEditRow` (chart drag 可否 guard) と
 *   `editing.savedBaseline` (KomaGrid の差列 SoT、komaBaselineSlots) に分離する
 *   (slice 1 では後者を view に含めず index が KomaGrid へ直接渡していたが、
 *   slice 2 で KomaGrid が view を消費するため view 側へ戻した)
 * - 48コマ表の参照列 (過去実績 + 確定履歴参照列) の concat・valueKind 付与・
 *   `adoptUnavailable` 判定 (D7、slice 2) — 「grid が描画する系列集合の決定」を
 *   chart 側の series と同じ builder に置く
 *
 * **callback (onDragEdit 等) と UI 一時状態 (hiddenKeys / highlightKoma / dragMode)
 * は含めない** (D1: 所有者〈Provider / index useState / page-model〉と更新頻度が
 * 値型と異なり、混ぜると pure な等値比較ができなくなるため)。
 *
 * page-model (`useForecastPageModel`) との責務分担 (D3): page-model =
 * 「編集/保存/確定できるか + その write 発火」(決定)、本 view = 「決定済みの値と
 * server data を描画用に断面像へ投影する」(render projection)。view は gate を
 * 再計算せず page-model 等の出力を field へ写すだけ。
 *
 * builder は index.tsx の単一 call site (`useMemo`) から呼ぶ (D2)。消費者は
 * ForecastCard / ForecastChart (slice 1) と KomaCard / KomaGrid (slice 2、D8/D9)。
 * KomaCard が自前で導出し続けるもの (sourceLabel / adoptDisabledReason の i18n
 * 文言、headBarHeight の実測) は presentation のため view に入れない (D8/D9)。
 */
import {
  AuxiliaryDataStatus,
  type PastActual,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'

import type { CivilDate } from '~/models/civilDate'
import type { GateCloseFrontier } from '~/models/gateClose'
import type { ResolvedConfirmRecordRefColumn } from '~/pages/DemandForecastDaily/models/confirmRecordRef'
import type {
  PastActualColumnDisabledReasonKey,
  PastActualOwnerBreakdownInput,
} from '~/pages/DemandForecastDaily/models/pastActualRetailerSelection'
import { selectPastActualColumn } from '~/pages/DemandForecastDaily/models/pastActualRetailerSelection'
import {
  hasChartTemperatureData,
  hasConsecutiveDrawableSlotValue,
  hasDefinedSlotValue,
  hasDewpointData as hasDewpointDataPredicate,
  hasDrawableSlotValue,
  komaToProtoSlots,
  protoSlotsToKoma,
} from '~/pages/DemandForecastDaily/models/slotHelpers'
import type {
  KomaSlots,
  RetailerEditRow,
  TargetScope,
} from '~/pages/DemandForecastDaily/models/types'

/**
 * KomaGrid の追加列の値の性質。'actual' = 過去実績 (託送実績由来、表示丸め
 * 対象)、'forecast' = 確定履歴参照列 (需要想定値、入力精度のまま表示)。
 * KomaGrid.tsx はこの型をそのまま使い、'actual' | 'forecast' の union を
 * 二重定義しない。
 */
export type ForecastReferenceColumnValueKind = 'actual' | 'forecast'

/**
 * KomaGrid の追加列 1 件分 (過去実績 or 確定履歴参照列、D7)。`id` /
 * `valueKind` は builder の入力 (`pastActualGridData` / `confirmRecordRefs`)
 * から必ず埋まるため required (KomaGrid 側にフォールバックを持たせない)。
 */
export type ForecastReferenceColumn = {
  /** 参照列「採用」(AC-DFD-047/048) の列識別キー。 */
  id: string
  label: string
  slots: KomaSlots
  valueKind: ForecastReferenceColumnValueKind
  /**
   * gate review (#6048): 集計スコープで per-retailer 解決対象が 0 件の列
   * (AC-DFD-032)。true の時は「採用」ボタンを描画しない (押しても no-op になる
   * 「表示と実行の乖離」を避ける)。
   */
  adoptUnavailable?: boolean
  /**
   * 過去実績列 (valueKind='actual') 限定の採用ボタン非活性理由 (需要想定 過去実績
   * 「採用」の小売別取込 Design Note Decision 3/9)。`adoptUnavailable` (非表示)
   * とは異なり、ボタンは描画したまま非活性 + tooltip で理由を示す — 押しても
   * no-op になる乖離を防ぎつつ「なぜ押せないか」を伝える。確定履歴列
   * (valueKind='forecast') では常に undefined (別機構の adoptUnavailable を使う)。
   */
  adoptDisabledReasonKey?: PastActualColumnDisabledReasonKey
}

/**
 * DemandForecastDaily 断面の描画専用値型。sub-group は D1/D7 準拠:
 * - `date`: 対象日の日付派生 (過去日ビューか)
 * - `series`: gate 済みの表示系列 10 個 + 48コマ表の参照列 (D7)
 * - `presence`: 系列存在述語 (ForecastCard 凡例 / ForecastChart 描画可否が共有)
 * - `cells`: per-koma の GC / 現在コマ / 手動編集コマ集合 / dirty コマ集合 (D7)
 * - `editing`: チャートドラッグ編集可否 + KomaGrid の差列 SoT (`savedBaseline`、
 *   前者とは同名別概念) + KomaGrid の read-only / 集計スコープ / 採用中参照列キー (D7)
 * - `targetLabel`: 対象名 (小売名 / "BG全体"、ForecastChart ツールチップ系列名専用)
 * - `status`: 外部データ取得ステータス由来の表示降格
 */
export type ForecastSectionView = {
  date: {
    /**
     * 過去日ビュー (reference view) か (= 対象日が JST の今日でない)。peak label の
     * 全 slot 算出切替に使う。
     */
    isReferenceView: boolean
  }
  series: {
    /** 表示対象の需要想定値 */
    forecast: KomaSlots
    /** 当日実績スロット (today_actual_slots)。当日断面のみ供給。 */
    todayActual?: KomaSlots
    /** 実績トレンド予測スロット (trend_preview.corrected_slots) */
    trendForecast?: KomaSlots
    /** 気温予測スロット */
    temperatureForecast?: KomaSlots
    /** 気温実績スロット */
    temperatureActual?: KomaSlots
    /** 露点実績スロット */
    dewpointActual?: KomaSlots
    preTemperatureActual?: KomaSlots
    preDewpointActual?: KomaSlots
    /**
     * AC-DFD-006a-1: 前端の需要想定 (前日 koma 47・48)。表示中スコープの前日の業務確定が
     * 無い時・帰属不明スコープは undefined。表示のみ (編集・ピーク注記・48 コマ表の対象外)。
     */
    preForecast?: KomaSlots
    /** AC-DFD-006a-1: 後端の需要想定 (翌日 koma 1〜6)。undefined の条件は preForecast と同じ。 */
    postForecast?: KomaSlots
    /** 過去実績 (FETCH_FAILED 時は D6 により空配列へ降格済み)。 */
    pastActuals: PastActual[]
    /** 確定履歴の参照列 (AC-DFD-031)。降格対象ではなくそのまま転送する。 */
    confirmRecordRefs: ResolvedConfirmRecordRefColumn[]
    /**
     * KomaGrid の追加列 (AC-01/AC-02 過去実績 + s07-confirm-history-import-
     * reference-view.md 確定履歴参照列、D7)。過去実績 (valueKind='actual') →
     * 確定履歴 (valueKind='forecast') の順で concat 済み (旧 index.tsx の順序と
     * 同じ)。**`series.pastActuals` (FETCH_FAILED 降格あり) から導出しない** —
     * grid の過去実績列は降格しない既存の非対称挙動を保存する (前提・制約参照)。
     * KomaGrid 側の defensive cap (10 件) はここでは適用しない (KomaGrid が担う)。
     */
    referenceColumns: ForecastReferenceColumn[]
  }
  presence: {
    hasTodayActual: boolean
    hasTrendForecast: boolean
    /** 右 YAxis (気温・露点) を実際に描くか。ForecastCard の WeatherBar.showRightAxis と共有。 */
    showTemperature: boolean
    hasTempForecast: boolean
    hasTempActual: boolean
    hasDewpointData: boolean
  }
  cells: {
    /** GCロック境界コマ番号 (koma_number.go 契約)。 */
    gcKoma: number
    /** 「今進行中のコマ」マーカー。当日以外は 0 (builder が gate、下記 input.currentKoma 参照)。 */
    currentKoma: number
    /**
     * チャートの GC 縦線 (AC-DFD-006a-5) を置く材料。対象日が JST の今日で、page の
     * GC 境界を受け取っている時だけ値を持つ (builder が gate)。線のコマは
     * `useChartGcLineKoma` が境界を現在時刻まで進めて求める。
     */
    gcLine: { frontier: GateCloseFrontier; targetDate: CivilDate } | undefined
    /**
     * 手動編集したコマ番号 (draft に永続化される概念、D7)。選択中小売行が無い
     * (`currentEditRow === undefined`、BG 全体ビュー等) 場合は空 Set。
     */
    manualEditKomas: Set<number>
    /**
     * 未保存 (dirty) コマ集合 (AC-02: baseline との差分視覚区別、D7)。
     * `manualEditKomas` とは別概念 — dirty は保存で解消されるが manualEditKomas
     * は保存後も残る (KomaGrid.tsx JSDoc 参照)。
     */
    dirtyKomas: Set<number>
  }
  editing: {
    /** 需要想定値凡例の「編集対象」バッジ / チャートドラッグ編集可否と同条件。 */
    isEditable: boolean
    /**
     * チャートドラッグ編集の可否 guard (`currentEditRow !== undefined`、または
     * 集計表示で全内訳へ按分して書き込める時〈AC-DFD-054、`canEditAggregate`〉)。
     * 比例配分の基準は `series.forecast` (= 編集前値のスナップショット) であり
     * `savedBaseline` (KomaGrid の差列 SoT) ではない (前提・制約の「同名別概念」
     * 注記参照)。
     */
    hasEditRow: boolean
    /**
     * 「確定値」列と同一の基準値 (KomaGrid.baselineSlots、差列の SoT = `komaBaselineSlots`)。
     * `hasEditRow` とは別概念 (前提・制約参照)。slice 2 で KomaGrid が view から
     * destructure する (D8)。
     */
    savedBaseline?: KomaSlots
    /**
     * KomaGrid セル / チャート / 参照列採用の read-only 基底 (page-model の
     * `forecastGates.isReadOnly`、D7)。
     */
    isReadOnly: boolean
    /**
     * 集計スコープ (BG 全体×配下複数、design note
     * tmp/designing-feature/demand-fix-aggregate-reference-adopt.md Decision
     * 1/6、D7)。`= !isRetailerEditableTarget`。true のとき過去実績列の「採用」
     * ボタンを KomaGrid が描画しない。
     */
    isAggregateScope: boolean
    /** 現在「採用中」の参照列キー (AC-DFD-047b: 同時に採用中は最大 1 列、D7)。 */
    adoptedReferenceColumnKey?: string
  }
  /**
   * 対象名 (小売名 / "BG全体")。ForecastChart のツールチップ系列名にのみ使う
   * (凡例の先頭系列ラベルは design 準拠の固定文言のため本 field を参照しない)。
   */
  targetLabel: string
  /**
   * 需要想定値列の取込元の材料 (KomaCard が i18n 文言に写す)。値変更を許さない
   * 時相 (`canEditNow=false`) では空 (undefined / false)。選択中小売 (配下 1 件の BG 全体を含む) は
   * その編集行の値。配下複数の BG 全体 (集計スコープ) は全配下小売の編集行から導出し、
   * `providerLabel` は全配下で同じ時だけ値を持ち (混在・取込元なしの小売が
   * 混じる時は undefined)、`isDraftMissing` は全配下に下書きが無い時だけ true。
   */
  source: {
    providerLabel: string | undefined
    isDraftMissing: boolean
  }
  status: {
    /** 過去実績取得が FETCH_FAILED か (Alert 表示 gate、D6)。 */
    pastActualsFailed: boolean
  }
}

/**
 * `buildForecastSectionView` の入力 (D2、D7)。
 *
 * 大半の入力は呼び出し元が解決済みの値をそのまま渡す (builder は再判定しない)。
 * 例外的に **builder 自身が gate する生値** が 3 つある — 呼び出し元は絞り込まずに
 * 渡すこと:
 * - `currentKoma`: `page?.currentKoma ?? 0` を生値のまま渡す。「当日以外は 0」
 *   (`cells.currentKoma` の invariant) は builder が `isTargetDateToday` で判定する
 * - `todayActualSlots`: `series.todayActual` としての可視性 (当日のみ表示) は
 *   builder が `isTargetDateToday` で判定する (`hasTodayActual` presence も同様)
 * - `trendForecastSlots`: `series.trendForecast` としての可視性は builder が
 *   `hasTrendReflect` で判定する
 *
 * それ以外の系列 (`temperatureForecastSlots` 等) は呼び出し元が「供給されうる
 * データ」をそのまま渡し、builder は series へ素通しする (可視性判定を持たない)。
 *
 * `pastActualGridData` / `confirmRecordRefs` (grid 向け参照列の元データ) は
 * `series.pastActuals` (chart 向け、FETCH_FAILED 降格あり) とは独立の入力 —
 * grid の過去実績列は降格しない既存の非対称を保つため、builder は
 * `series.referenceColumns` を `series.pastActuals` から導出しない。
 */
export type BuildForecastSectionViewInput = {
  targetScope: TargetScope
  isTargetDateToday: boolean
  // editor (displaySlots, currentEditRow, displayLabel)
  displaySlots: KomaSlots
  currentEditRow: RetailerEditRow | undefined
  displayLabel: string
  // gates (canImportToDraft, komaBaselineSlots)
  isEditableTarget: boolean
  /** 集計表示 (BG全体 × 配下複数) で全内訳へ按分して編集できるか (`forecastGates.canEditAggregate`)。 */
  canEditAggregate: boolean
  komaBaselineSlots?: KomaSlots
  // page / gridData 由来の系列
  todayActualSlots?: KomaSlots
  temperatureForecastSlots?: KomaSlots
  temperatureActualSlots?: KomaSlots
  dewpointActualSlots?: KomaSlots
  preTemperatureActualSlots?: KomaSlots
  preDewpointActualSlots?: KomaSlots
  /** 表示中スコープの前日の需要想定 (AC-DFD-006a-1)。series へ素通しする。 */
  preForecastSlots?: KomaSlots
  /** 表示中スコープの翌日の需要想定 (AC-DFD-006a-1)。series へ素通しする。 */
  postForecastSlots?: KomaSlots
  pastActuals: PastActual[]
  pastActualsStatus?: AuxiliaryDataStatus
  // index 所有の flow 出力 (confirmRecordRefColumns, trendForecastKomaSlots + hasTrendReflect)
  confirmRecordRefs: ResolvedConfirmRecordRefColumn[]
  trendForecastSlots?: KomaSlots
  hasTrendReflect: boolean
  // page 由来の per-koma cell。gcKoma は gate 不要のためそのまま写す。
  // currentKoma は生値 (`page?.currentKoma ?? 0`) を渡す — 「当日以外は 0」
  // (cells.currentKoma の invariant) は builder が isTargetDateToday で gate する
  // (旧 index.tsx の `isTargetDateToday ? (page?.currentKoma ?? 0) : 0` をここへ
  // 一本化。呼び出し元で gate 済みの値を渡さないこと)。
  gcKoma: number
  currentKoma: number
  /** page を作った時刻の GC 境界 (`gate_close_frontier`)。page 取得前は undefined。 */
  gateCloseFrontier: GateCloseFrontier | undefined
  /** 対象日 (wfCase.targetDate)。workflow 取得前は undefined。 */
  targetDate: CivilDate | undefined
  // 48コマ表 (KomaGrid) 向け入力 (D7、slice 2)
  /**
   * 過去実績の grid 列 (`useDemandForecastGridData.pastActualGridData`)。
   * `series.referenceColumns` の過去実績部分 (valueKind='actual') の元データ。
   * chart 向け `pastActuals` (PastActual[]、FETCH_FAILED 降格あり) とは独立。
   * `retailerSlots` / `ownerBreakdownAvailable` は builder が
   * `selectPastActualColumn` で選択する (Decision 2/6、下記参照)。
   */
  pastActualGridData: Array<
    { id: string; label: string } & PastActualOwnerBreakdownInput
  >
  /** AC-02: page-model の gate (`forecastGates.effectiveDirtyKomas`) をそのまま写す。 */
  effectiveDirtyKomas: Set<number>
  /** KomaGrid セル / チャート / 参照列採用の read-only 基底 (`forecastGates.isReadOnly`)。 */
  isReadOnly: boolean
  /**
   * 集計スコープ (`!isRetailerEditableTarget`)。呼び出し元 (index.tsx) が既存の
   * local 変数をそのまま渡す (builder は再導出しない、D7)。
   */
  isAggregateScope: boolean
  /** 現在「採用中」の参照列キー (`effectiveAdoptedReferenceColumnKey`)。 */
  adoptedReferenceColumnKey?: string
  /** 値変更を許す時相か (`forecastGates.canEditNow`)。false なら取込元を出さない。 */
  canEditNow: boolean
  /** 全配下小売の編集行 (集計スコープの取込元 `source` の導出元)。 */
  editRows: Record<string, RetailerEditRow>
  /**
   * gate review (#6048): 確定履歴参照列 id → 集計スコープでの per-retailer
   * 解決対象数 (index.tsx が `resolveAggregateTargetSlots` の走査結果を 1 回だけ
   * memo し、「採用中」判定と共有する)。builder は data → data の pure builder
   * であり続けるため、関数ではなく Map で受ける。Map に無い列は 0 件扱いで、
   * 集計スコープなら `adoptUnavailable=true` になる (AC-DFD-032)。
   */
  aggregateAdoptTargetCounts: ReadonlyMap<string, number>
}

/**
 * `ForecastSectionView` を組み立てる pure builder。index.tsx の単一 call site
 * (`useMemo`) から呼ぶ (D2)。gate 式・存在述語の再計算は行わず、各 field はここで
 * 決まった値をそのまま consumer (ForecastCard / ForecastChart / KomaCard /
 * KomaGrid) が読む。
 */
export function buildForecastSectionView(
  input: BuildForecastSectionViewInput,
): ForecastSectionView {
  const isReferenceView = !input.isTargetDateToday

  // 「今進行中のコマ」マーカーは当日以外意味を持たないため、当日以外は 0 に
  // gate する (cells.currentKoma の invariant。呼び出し元は生値を渡す)。
  const currentKoma = input.isTargetDateToday ? input.currentKoma : 0

  // D6: pastActuals の FETCH_FAILED 降格。core の需要想定値は表示維持し、
  // 過去実績系列のみ空にする (Alert 表示は status.pastActualsFailed が gate)。
  const pastActualsFailed =
    input.pastActualsStatus === AuxiliaryDataStatus.FETCH_FAILED
  const visiblePastActuals = pastActualsFailed ? [] : input.pastActuals

  // Decision 2/6 (demand-forecast-adopt-actual-per-retailer.md): チャート系列
  // (series.pastActuals) にも grid 列 (series.referenceColumns) と同じ選択規則
  // (selectPastActualColumn) を適用する。帰属不明は専用scopeとして扱い、それ以外は
  // 集計表示か小売表示かを使い分ける。小売の選択キーは
  // currentEditRow.retailerBusinessCd を使う — page-level の
  // selectedRetailerBusinessCd は配下 1 件の BG 全体で undefined のままだが、
  // currentEditRow はその場合も唯一の小売の行を指す (前提6)。
  const pastActualTargetScope =
    input.targetScope === 'UNATTRIBUTED'
      ? 'UNATTRIBUTED'
      : input.isAggregateScope
        ? 'BG_TOTAL'
        : 'RETAILER'
  const retailerBusinessCd = input.currentEditRow?.retailerBusinessCd
  // selectPastActualColumn は KomaSlots 入出力に統一されている
  // (pastActualRetailerSelection.ts JSDoc 参照)。series.pastActuals の消費者
  // (ForecastChart 等) は PastActual[] のまま受け取る契約を変えないため、
  // proto SlotValue[] ⇔ KomaSlots をここで変換して .slots だけ差し替える。
  const resolvedPastActuals: PastActual[] = visiblePastActuals.map((pa) => ({
    ...pa,
    slots: komaToProtoSlots(
      selectPastActualColumn(
        {
          slots: protoSlotsToKoma(pa.slots),
          unattributedSlots: protoSlotsToKoma(pa.unattributedSlots),
          retailerSlots: pa.retailerSlots.map((rs) => ({
            retailerBusinessCd: rs.retailerBusinessCd,
            slots: protoSlotsToKoma(rs.slots),
          })),
          ownerBreakdownAvailable: pa.ownerBreakdownAvailable,
        },
        pastActualTargetScope,
        retailerBusinessCd,
      ).slots,
    ),
  }))

  // 当日実績は当日断面のみ、実績トレンド予測は hasTrendReflect の時のみ
  // (旧 index.tsx forecastCard/komaCard object の二重 gate をここへ一本化)。
  const todayActual = input.isTargetDateToday
    ? input.todayActualSlots
    : undefined
  const trendForecast = input.hasTrendReflect
    ? input.trendForecastSlots
    : undefined

  // ForecastCard (凡例構築) と ForecastChart (Line/Area 描画可否) が同一 helper を
  // 同一入力で呼ぶことで判定の二重導出によるズレを防ぐ (slotHelpers.ts JSDoc 参照)。
  const combinedTemperatureActual: KomaSlots = [
    ...(input.preTemperatureActualSlots ?? []),
    ...(input.temperatureActualSlots ?? []),
  ]
  const combinedDewpointActual: KomaSlots = [
    ...(input.preDewpointActualSlots ?? []),
    ...(input.dewpointActualSlots ?? []),
  ]

  const showTemperature = hasChartTemperatureData({
    temperatureForecastSlots: input.temperatureForecastSlots,
    temperatureActualSlots: combinedTemperatureActual,
    dewpointActualSlots: combinedDewpointActual,
    pastActuals: visiblePastActuals,
  })
  const hasTempForecast = hasDrawableSlotValue(input.temperatureForecastSlots)
  const hasTempActual = hasConsecutiveDrawableSlotValue(
    combinedTemperatureActual,
  )
  const hasDewpointData = hasDewpointDataPredicate({
    dewpointActualSlots: combinedDewpointActual,
    pastActuals: visiblePastActuals,
  })

  // D7: 48コマ表の参照列。過去実績 (valueKind='actual') → 確定履歴
  // (valueKind='forecast') の順で concat する (旧 index.tsx komaCard object の
  // pastActualSlots 配列と同じ順序)。series.pastActuals (FETCH_FAILED 降格あり)
  // からは導出しない (前提・制約の非対称を保存)。過去実績列の表示値・採用
  // 非活性理由は selectPastActualColumn がチャート系列と同じ規則で決める
  // (Decision 2/3/6/9)。
  const referenceColumns: ForecastReferenceColumn[] = [
    ...input.pastActualGridData.map((pa): ForecastReferenceColumn => {
      const resolved = selectPastActualColumn(
        pa,
        pastActualTargetScope,
        retailerBusinessCd,
      )
      return {
        id: pa.id,
        label: pa.label,
        slots: resolved.slots,
        valueKind: 'actual',
        adoptDisabledReasonKey: resolved.disabledReasonKey,
      }
    }),
    ...input.confirmRecordRefs.map(
      (col): ForecastReferenceColumn => ({
        id: col.id,
        label: col.label,
        slots: col.slots,
        valueKind: 'forecast',
        // gate review (#6048): 集計スコープで解決対象が 0 件の列は採用ボタンを
        // 非表示にする (AC-DFD-032、押しても no-op になる乖離を避ける)。
        adoptUnavailable:
          input.isAggregateScope &&
          (input.aggregateAdoptTargetCounts.get(col.id) ?? 0) === 0,
      }),
    ),
  ]

  return {
    date: {
      isReferenceView,
    },
    series: {
      forecast: input.displaySlots,
      todayActual,
      trendForecast,
      temperatureForecast: input.temperatureForecastSlots,
      temperatureActual: input.temperatureActualSlots,
      dewpointActual: input.dewpointActualSlots,
      preTemperatureActual: input.preTemperatureActualSlots,
      preDewpointActual: input.preDewpointActualSlots,
      preForecast: input.preForecastSlots,
      postForecast: input.postForecastSlots,
      pastActuals: resolvedPastActuals,
      confirmRecordRefs: input.confirmRecordRefs,
      referenceColumns,
    },
    presence: {
      hasTodayActual: hasDefinedSlotValue(todayActual),
      hasTrendForecast: hasDefinedSlotValue(trendForecast),
      showTemperature,
      hasTempForecast,
      hasTempActual,
      hasDewpointData,
    },
    cells: {
      gcKoma: input.gcKoma,
      currentKoma,
      gcLine:
        input.isTargetDateToday &&
        input.gateCloseFrontier !== undefined &&
        input.targetDate !== undefined
          ? { frontier: input.gateCloseFrontier, targetDate: input.targetDate }
          : undefined,
      manualEditKomas: input.currentEditRow?.manualEditKomas ?? new Set(),
      dirtyKomas: input.effectiveDirtyKomas,
    },
    editing: {
      isEditable: input.isEditableTarget,
      hasEditRow: input.currentEditRow !== undefined || input.canEditAggregate,
      savedBaseline: input.komaBaselineSlots,
      isReadOnly: input.isReadOnly,
      isAggregateScope: input.isAggregateScope,
      adoptedReferenceColumnKey: input.adoptedReferenceColumnKey,
    },
    targetLabel: input.displayLabel,
    source: deriveSource(input),
    status: {
      pastActualsFailed,
    },
  }
}

/**
 * 取込元 (`ForecastSectionView.source`) の導出 (AC-DFD-001a, #10421)。
 * 値変更を許さない時相 (過去日 / 確定済み、`canEditNow=false`) は出さない。
 * 集計ロック (未入力の内訳あり) は抑止しない。集計スコープは全配下小売の編集行を畳む
 * (選択行があるのは非集計スコープのみ)。
 */
function deriveSource(
  input: BuildForecastSectionViewInput,
): ForecastSectionView['source'] {
  if (!input.canEditNow) {
    return { providerLabel: undefined, isDraftMissing: false }
  }
  if (!input.isAggregateScope) {
    return {
      providerLabel: input.currentEditRow?.providerLabel,
      isDraftMissing: input.currentEditRow?.draftUpdatedAt === undefined,
    }
  }
  const rows = Object.values(input.editRows)
  const labels = new Set(rows.map((row) => row.providerLabel))
  const [only] = labels
  return {
    providerLabel: labels.size === 1 ? only : undefined,
    isDraftMissing:
      rows.length > 0 && rows.every((row) => row.draftUpdatedAt === undefined),
  }
}
